import React, { useState, useEffect, forwardRef, useCallback } from 'react'
import {
  Card,
  Text,
  Stack,
  Flex,
  Box,
  Heading,
  TextInput,
  Spinner,
  Label,
} from '@sanity/ui'
import { set, setIfMissing } from 'sanity'
import { useClient } from 'sanity'
import { ArrayOfObjectsInputProps } from 'sanity'

interface ScoringCriteria {
  _id: string
  name: string
}

interface FitmentType {
  _id: string
  name: string
  slug?: any
}

interface WeightValue {
  _key: string
  scoreCriteria: {
    _type: 'reference'
    _ref: string
  }
  fitment: {
    _type: 'reference'
    _ref: string
  }
  weight: number
}

const ScoringWeightsInput = forwardRef<HTMLDivElement, ArrayOfObjectsInputProps>(
  (props, ref) => {
    const { value, readOnly, onChange, schemaType } = props

    const [scoringCriteria, setScoringCriteria] = useState<ScoringCriteria[]>([])
    const [fitmentTypes, setFitmentTypes] = useState<FitmentType[]>([])
    const [fitmentWeights, setFitmentWeights] = useState<
      Record<string, Record<string, number>>
    >({})
    const [isLoadingScoringCriteria, setIsLoadingScoringCriteria] = useState(true)
    const [isLoadingFitments, setIsLoadingFitments] = useState(true)
    const [error, setError] = useState<string | null>(null)

    const client = useClient({ apiVersion: '2023-05-03' })

    // Fetch fitment types and scoring criteria
    useEffect(() => {
      let mounted = true

      const fetchFitmentTypes = async () => {
        try {
          setIsLoadingFitments(true)
          const query = `*[_type == 'fitment']{ _id, name, slug }`
          const result = await client.fetch<FitmentType[]>(query)
          if (mounted) {
            setFitmentTypes(result.sort((a, b) => a.name.localeCompare(b.name)))
          }
        } catch (err) {
          if (mounted) {
            setError('Failed to load fitment types')
            console.error('Error fetching fitment types:', err)
          }
        } finally {
          if (mounted) {
            setIsLoadingFitments(false)
          }
        }
      }

      const fetchScoringCriteria = async () => {
        try {
          setIsLoadingScoringCriteria(true)
          const query = `*[_type == 'scoringCriteria']{ _id, name }`
          const result = await client.fetch<ScoringCriteria[]>(query)
          if (mounted) {
            setScoringCriteria(result.sort((a, b) => a.name.localeCompare(b.name)))
          }
        } catch (err) {
          if (mounted) {
            setError('Failed to load Scoring Criteria')
            console.error('Error fetching Scoring Criteria:', err)
          }
        } finally {
          if (mounted) {
            setIsLoadingScoringCriteria(false)
          }
        }
      }

      fetchFitmentTypes()
      fetchScoringCriteria()

      return () => {
        mounted = false
      }
    }, [client])

    const isLoading = isLoadingFitments || isLoadingScoringCriteria

    // Convert array to a map for easier lookup
    const convertArrayToMap = (arr: WeightValue[]) => {
      const map: Record<string, Record<string, number>> = {}
      if (!arr || !Array.isArray(arr)) return map

      arr.forEach((item) => {
        if (
          !item.scoreCriteria?._ref ||
          !item.fitment?._ref ||
          item.weight === undefined
        )
          return

        const criteriaId = item.scoreCriteria._ref
        const fitmentId = item.fitment._ref

        if (!map[criteriaId]) {
          map[criteriaId] = {}
        }

        map[criteriaId][fitmentId] = item.weight
      })

      return map
    }

    // Convert map back to array format
    const convertMapToArray = (
      map: Record<string, Record<string, number>>,
    ): WeightValue[] => {
      const arr: WeightValue[] = []

      Object.keys(map).forEach((criteriaId) => {
        Object.keys(map[criteriaId]).forEach((fitmentId) => {
          arr.push({
            _key: `${criteriaId}-${fitmentId}`,
            scoreCriteria: {
              _type: 'reference',
              _ref: criteriaId,
            },
            fitment: {
              _type: 'reference',
              _ref: fitmentId,
            },
            weight: map[criteriaId][fitmentId],
          })
        })
      })

      return arr
    }

    // Initialize weights from value
    useEffect(() => {
      if (value && Array.isArray(value)) {
        setFitmentWeights(convertArrayToMap(value as WeightValue[]))
      } else {
        onChange(setIfMissing([]))
      }
    }, [value, onChange])

    // Handle weight change
    const handleWeightChange = useCallback(
      (criteriaId: string, fitmentId: string, newValue: string) => {
        const updatedWeights = { ...(fitmentWeights || {}) }

        if (!updatedWeights[criteriaId]) {
          updatedWeights[criteriaId] = {}
        }

        if (newValue === '') {
          delete updatedWeights[criteriaId][fitmentId]
          if (Object.keys(updatedWeights[criteriaId]).length === 0) {
            delete updatedWeights[criteriaId]
          }
        } else {
          const numValue = parseFloat(newValue)
          if (!isNaN(numValue)) {
            updatedWeights[criteriaId][fitmentId] = numValue
          }
        }

        setFitmentWeights(updatedWeights)
        const weightsArray = convertMapToArray(updatedWeights)
        onChange(set(weightsArray))
      },
      [fitmentWeights, onChange],
    )

    // Get current weight value
    const getWeightValue = (criteriaId: string, fitmentId: string) => {
      if (
        !fitmentWeights ||
        !fitmentWeights[criteriaId] ||
        fitmentWeights[criteriaId][fitmentId] === undefined
      ) {
        return ''
      }
      return fitmentWeights[criteriaId][fitmentId].toString()
    }

    return (
      <Stack gap={3} ref={ref}>
        <Box>
          <Label size={1}>{schemaType.title}</Label>
          {schemaType.description && (
            <Box paddingTop={2}>
              <Label size={0} muted>
                {schemaType.description}
              </Label>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Flex align="center" justify="center" padding={4}>
            <Spinner muted />
            <Box marginLeft={3}>
              <Text>Loading data...</Text>
            </Box>
          </Flex>
        ) : error ? (
          <Card tone="critical" padding={4}>
            <Text>{error}</Text>
          </Card>
        ) : (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Heading size={1}>Scoring Weights Configuration</Heading>
              <Text size={1} muted>
                Configure weights for each combination of scoring criteria and fitment
                type.
              </Text>

              <Box overflow="auto">
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #ccc',
                          textAlign: 'left',
                        }}
                      >
                        Fitment / Criteria
                      </th>
                      {scoringCriteria.map((criteria) => (
                        <th
                          key={criteria._id}
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #ccc',
                            textAlign: 'center',
                            minWidth: '120px',
                          }}
                        >
                          {criteria.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fitmentTypes.map((fitment) => (
                      <tr key={fitment._id}>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            fontWeight: 'bold',
                          }}
                        >
                          {fitment.name}
                        </td>
                        {scoringCriteria.map((criteria) => (
                          <td
                            key={`${fitment._id}-${criteria._id}`}
                            style={{
                              padding: '8px',
                              borderBottom: '1px solid #eee',
                              textAlign: 'center',
                            }}
                          >
                            <TextInput
                              type="number"
                              step="1"
                              placeholder="0"
                              max={100}
                              min={0}
                              value={getWeightValue(criteria._id, fitment._id)}
                              onChange={(event) =>
                                handleWeightChange(
                                  criteria._id,
                                  fitment._id,
                                  event.currentTarget.value,
                                )
                              }
                              disabled={readOnly}
                              style={{ width: '100px' }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Stack>
          </Card>
        )}
      </Stack>
    )
  },
)

ScoringWeightsInput.displayName = 'ScoringWeightsInput'

export default ScoringWeightsInput
